import { motion } from "framer-motion";
import { useLenis } from "lenis/react";
import { Sparkle } from "lucide-react";
import { IMAGES } from "@/constants/site";

const EASE = [0.22, 1, 0.36, 1];

const DETAILS = ["69 instructional cards", "22 timeline cards", "Accompanying guidebook", 'By Carlena "Umi" Jenkins'];

export default function Deck() {
  const lenis = useLenis();

  return (
    <section id="deck" data-testid="deck-section" className="relative overflow-hidden bg-[#0D0B0A] py-28 md:py-40">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 md:px-12 lg:grid-cols-2 lg:gap-24">
        <motion.div
          initial={{ opacity: 0, y: 40, rotate: -2 }}
          whileInView={{ opacity: 1, y: 0, rotate: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.1, ease: EASE }}
          className="relative order-2 lg:order-1"
        >
          <div className="glow-flicker absolute -inset-8 rounded-full bg-[#8C382A] opacity-20 blur-[100px]" aria-hidden="true" />
          <div className="relative overflow-hidden border border-[#D49A36]/20 shadow-[0_0_60px_rgba(140,56,42,0.2)]">
            <motion.img
              data-testid="deck-image"
              src={IMAGES.deck}
              alt="The Consecration Oracle deck beside card 21, 'Wearing Black', and a '24 Hours' timeline card"
              className="aspect-[4/5] w-full object-cover"
              initial={{ scale: 1.1 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.6, ease: EASE }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D0B0A]/70 via-transparent to-transparent" />
            <p className="absolute bottom-5 left-5 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#D49A36]">
              The Consecration Oracle — in hand
            </p>
          </div>
        </motion.div>

        <div className="order-1 lg:order-2">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="mb-5 text-[11px] font-semibold uppercase tracking-[0.3em] text-[#D49A36] md:text-xs"
          >
            The Deck
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
            className="font-display text-4xl font-medium leading-[1.02] tracking-tight text-[#F2EFE9] md:text-6xl"
          >
            The <span className="italic text-[#D49A36]">Consecration</span> Oracle
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
            className="mt-8 max-w-lg text-base leading-relaxed text-[#DCD8D3] md:text-lg"
          >
            A divination tool for exploring intentional abstinence, discipline, and spiritual practice. Through
            69 instructional cards and an accompanying set of timeline cards, the deck invites you to identify
            what may need to be limited or released — and, when appropriate, enter a <em className="text-[#F2EFE9]">Living Fast</em> to
            discover what becomes possible in its absence.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
            className="mt-8 border-l-2 border-[#D49A36]/60 pl-6 font-display text-2xl italic leading-snug text-[#D49A36] md:text-3xl"
          >
            This isn't about deprivation. It's about making room.
          </motion.p>

          <ul className="mt-10 space-y-4">
            {DETAILS.map((detail, i) => (
              <motion.li
                key={detail}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: 0.35 + i * 0.1, ease: EASE }}
                className="flex items-center gap-4 text-sm text-[#DCD8D3] md:text-base"
              >
                <Sparkle className="h-4 w-4 shrink-0 text-[#D49A36]" strokeWidth={1.5} />
                {detail}
              </motion.li>
            ))}
          </ul>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
            className="mt-12"
          >
            <button
              data-testid="deck-waitlist-button"
              onClick={() => lenis?.scrollTo("#making-space", { duration: 1.6 })}
              className="bg-[#D49A36] px-8 py-4 text-xs font-bold uppercase tracking-[0.22em] text-[#131110] shadow-[0_0_40px_rgba(212,154,54,0.25)] transition-all duration-300 hover:bg-[#F2EFE9] hover:shadow-[0_0_50px_rgba(212,154,54,0.35)]"
            >
              Join the waitlist
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
